import { useMemo, useState } from "react";
import type {
  ActiveReloadView,
  QlikReloadRow,
} from "@qlik-scheduler/shared";

import { ApiError } from "../../api/client.js";
import {
  useActiveReloads,
  useCancelReload,
  useConnections,
  useQlikReloads,
} from "../../api/queries.js";
import { Button } from "../../components/Button.js";
import { ConfirmDialog } from "../../components/ConfirmDialog.js";
import { ErrorAlert } from "../../components/ErrorAlert.js";
import { Skeleton } from "../../components/Skeleton.js";
import { StatusBadge } from "../../components/StatusBadge.js";
import { useToast } from "../../components/Toast.js";

// Unified row shape for both feeds. `localId` is set when the row is in our
// DB (cancellable); `qlikId` is always set so the merge dedup'es by it.
interface UnifiedRow {
  key: string;
  appName: string;
  status: ActiveReloadView["status"];
  startTime: string;
  source: QlikReloadRow["source"];
  progressPercentage: number; // 0..100; 0 for external rows we don't track
  retryAttempt: number | null;
  reloadType: ActiveReloadView["reloadType"] | null;
  localReloadId: string | null;
  qlikReloadId: string | null;
}

export function ActiveReloadsTab() {
  const local = useActiveReloads();
  const connectionsQ = useConnections();
  const [includeExternal, setIncludeExternal] = useState(false);

  // Fetch Qlik's in-flight list per-connection when the toggle is on. We
  // fan out to ALL active connections — the user explicitly opted in by
  // toggling, so polling every tenant is acceptable cost.
  const connectionIds = useMemo(
    () =>
      (connectionsQ.data ?? [])
        .filter((c) => c.isActive)
        .map((c) => c.id),
    [connectionsQ.data],
  );
  const qlik = useQlikReloads(connectionIds, "active", {
    enabled: includeExternal,
    refetchInterval: includeExternal ? 5_000 : false,
  });

  const cancel = useCancelReload();
  const toast = useToast();
  const [confirm, setConfirm] = useState<UnifiedRow | null>(null);

  // Build the unified view. When the toggle is OFF we just render local
  // active reloads. When ON we merge: Qlik's list is the union, local rows
  // contribute progressPercentage + retryAttempt where matched.
  const rows: UnifiedRow[] = useMemo(() => {
    const localRows = local.data ?? [];
    if (!includeExternal) {
      return localRows.map((r) => ({
        key: r.reloadId,
        appName: r.appName,
        status: r.status,
        startTime: r.startTime,
        source: "qlik-scheduler" as const,
        progressPercentage: r.progressPercentage,
        retryAttempt: r.retryAttempt,
        reloadType: r.reloadType,
        localReloadId: r.reloadId,
        qlikReloadId: r.qlikReloadId,
      }));
    }
    // Merge — index local by qlikReloadId for enrichment.
    const localByQlikId = new Map(
      localRows.map((r) => [r.qlikReloadId, r] as const),
    );
    const out: UnifiedRow[] = [];
    for (const q of qlik.data?.data ?? []) {
      const localMatch = localByQlikId.get(q.qlikReloadId);
      out.push({
        key: q.qlikReloadId,
        appName:
          localMatch?.appName ??
          q.appName ??
          `App ${q.appId.slice(0, 8)}…`,
        status: q.status,
        startTime: q.startTime ?? new Date().toISOString(),
        source: q.source,
        progressPercentage: localMatch?.progressPercentage ?? 0,
        retryAttempt: localMatch?.retryAttempt ?? null,
        reloadType: localMatch?.reloadType ?? null,
        localReloadId: localMatch?.reloadId ?? null,
        qlikReloadId: q.qlikReloadId,
      });
    }
    // Add any local rows Qlik didn't list (shouldn't happen often — small
    // race window between our trigger and Qlik's list).
    for (const r of localRows) {
      if (!out.find((row) => row.qlikReloadId === r.qlikReloadId)) {
        out.push({
          key: r.reloadId,
          appName: r.appName,
          status: r.status,
          startTime: r.startTime,
          source: "qlik-scheduler" as const,
          progressPercentage: r.progressPercentage,
          retryAttempt: r.retryAttempt,
          reloadType: r.reloadType,
          localReloadId: r.reloadId,
          qlikReloadId: r.qlikReloadId,
        });
      }
    }
    out.sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());
    return out;
  }, [local.data, qlik.data, includeExternal]);

  const isLoading = local.isLoading && !local.data;
  const error = local.error ?? (includeExternal ? qlik.error : null);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[12px] text-ink-soft">
          {includeExternal
            ? "Showing all in-flight reloads on the tenant — auto-refreshing every 5s."
            : "Showing reloads triggered from here — auto-refreshing every 5s."}
        </p>
        <label className="inline-flex items-center gap-2 text-sm text-ink">
          <input
            type="checkbox"
            checked={includeExternal}
            onChange={(e) => setIncludeExternal(e.target.checked)}
            className="h-4 w-4 rounded border-line text-blue focus:ring-blue"
          />
          Include external reloads
        </label>
      </div>

      {error ? <ErrorAlert>{describeError(error)}</ErrorAlert> : null}

      {isLoading ? (
        <div className="space-y-3" aria-label="Loading active reloads">
          {Array.from({ length: 2 }, (_, i) => (
            <div key={i} className="card p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 space-y-2">
                  <Skeleton width="40%" height={14} />
                  <Skeleton width="30%" height={11} />
                </div>
                <Skeleton width={90} height={32} rounded="full" />
              </div>
              <Skeleton className="mt-4" height={8} rounded="full" />
            </div>
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="card empty-surface">
          <p className="text-base font-semibold text-ink">Nothing in flight</p>
          <p>
            {includeExternal
              ? "No reloads are currently running on any tenant."
              : "No reloads triggered from here are currently running."}
          </p>
        </div>
      ) : (
        rows.map((r) => (
          <article key={r.key} className="card p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h4 className="truncate text-sm font-semibold text-ink">
                    {r.appName}
                  </h4>
                  <StatusBadge tone={statusTone(r.status)}>
                    {r.status}
                  </StatusBadge>
                  <SourceBadge source={r.source} />
                  {r.reloadType ? (
                    <StatusBadge tone="grey">{r.reloadType}</StatusBadge>
                  ) : null}
                </div>
                <p className="mt-1 text-[12px] text-ink-soft">
                  Started {formatRelative(r.startTime)}
                  {r.retryAttempt != null ? ` · attempt #${r.retryAttempt}` : ""}
                </p>
              </div>
              {r.localReloadId ? (
                <Button
                  variant="danger-sm"
                  onClick={() => setConfirm(r)}
                  disabled={
                    cancel.isPending && cancel.variables === r.localReloadId
                  }
                >
                  Cancel
                </Button>
              ) : (
                <span
                  className="text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-soft"
                  title="Not triggered from here — cancel in the Qlik tenant"
                >
                  External
                </span>
              )}
            </div>
            {r.localReloadId ? (
              <ProgressBar value={r.progressPercentage} />
            ) : null}
          </article>
        ))
      )}

      <ConfirmDialog
        open={confirm != null}
        title="Cancel reload"
        message={
          confirm ? (
            <p>
              Cancel the in-flight reload of <strong>{confirm.appName}</strong>?
              The Qlik tenant may finish the current task before stopping.
            </p>
          ) : null
        }
        confirmLabel="Cancel reload"
        destructive
        busy={cancel.isPending}
        onCancel={() => setConfirm(null)}
        onConfirm={async () => {
          if (!confirm || !confirm.localReloadId) return;
          try {
            await cancel.mutateAsync(confirm.localReloadId);
            toast.push("success", "Cancellation requested");
            setConfirm(null);
          } catch (err) {
            toast.push("error", describeError(err));
          }
        }}
      />
    </div>
  );
}

function SourceBadge({ source }: { source: QlikReloadRow["source"] }) {
  switch (source) {
    case "qlik-scheduler":
      return <StatusBadge tone="blue">qlik-scheduler</StatusBadge>;
    case "hub":
      return <StatusBadge tone="yellow">Hub</StatusBadge>;
    case "qlik-native":
      return <StatusBadge tone="grey">Qlik scheduler</StatusBadge>;
    case "external":
      return <StatusBadge tone="grey">External</StatusBadge>;
  }
}

function ProgressBar({ value }: { value: number }) {
  const clamped = Math.max(0, Math.min(100, value));
  return (
    <div className="mt-4">
      <div className="h-2 w-full overflow-hidden rounded-full bg-line">
        <div
          role="progressbar"
          aria-valuenow={clamped}
          aria-valuemin={0}
          aria-valuemax={100}
          className="h-full rounded-full bg-blue transition-all"
          style={{ width: `${clamped}%` }}
        />
      </div>
      <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-soft">
        {clamped}%
      </p>
    </div>
  );
}

function statusTone(s: ActiveReloadView["status"]) {
  switch (s) {
    case "SUCCEEDED":
      return "green";
    case "FAILED":
      return "red";
    case "CANCELLED":
      return "grey";
    case "QUEUED":
      return "blue";
    case "RELOADING":
      return "blue";
  }
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "recently";
  const seconds = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return new Date(iso).toLocaleString();
}

function describeError(err: unknown): string {
  if (err instanceof ApiError) return `${err.code}: ${err.message}`;
  if (err instanceof Error) return err.message;
  return "Unknown error";
}
